import { http } from '@/core/api'
import type { FactoryConfirmStatus } from '@/shared/utils/factory-confirm-status'

export type EmploymentVerificationOutcome =
  | 'on_job'
  | 'suspected_left'
  | 'left_seeking'
  | 'left_not_seeking'

export type EmploymentVerificationMethod = 'phone' | 'video' | 'zalo'

export type EmploymentVerification = {
  id: number
  employment_record_id: number
  worker_id: number
  application_id: number | null
  outcome: EmploymentVerificationOutcome
  source: string
  verify_method: EmploymentVerificationMethod | null
  confidence: number
  note: string | null
  next_follow_at: string | null
  created_at: string | null
  verifier: { id: number; name: string } | null
}

export type RecallProtection = {
  id: number
  status: string
  started_at: string | null
  ends_at: string | null
  days_remaining: number | null
}

export type ActiveEmploymentMilestoneTracking = {
  day: number
  target_date: string | null
  status: 'pending' | 'reached'
}

export type ActiveEmploymentRow = {
  id: number
  employment_record_id: number
  worker_id: number
  payroll_absence_streak?: number
  last_payroll_period?: string | null
  application_id: number | null
  entry_date: string | null
  factory_employee_code?: string | null
  factory_status: string
  status_source: string | null
  status_confidence: string | null
  last_verified_at: string | null
  leave_date: string | null
  leave_reason: string | null
  days_worked: number | null
  days_on_job: number | null
  can_verify?: boolean
  can_create_follow_up?: boolean
  worker: {
    id: number
    worker_code: string | null
    full_name: string | null
    phone_masked: string | null
    identity_number_display?: string | null
  } | null
  client: { id: number; name: string } | null
  job_order: {
    id: number
    code: string | null
    title: string | null
    position_title?: string | null
    client_site_id?: number | null
    client_site_name?: string | null
  } | null
  recruiter: { id: number; name: string } | null
  responsible: {
    onsite: { id: number; name: string | null } | null
    recruiter: { id: number; name: string | null } | null
  }
  milestone_tracking: ActiveEmploymentMilestoneTracking[]
  hired_at: string | null
  latest_verification: EmploymentVerification | null
  next_follow_at: string | null
  recall_protection: RecallProtection | null
  verification_count?: number
}

export type ActiveEmploymentDashboard = {
  active_count: number
  verified_in_period: number
  never_verified: number
  latest_on_job: number
  latest_suspected_left: number
  latest_left_seeking: number
  period_from: string
  period_to: string
}

export type ActiveEmploymentSummary = {
  total: number
  latest_outcomes: Array<{ key: string; count: number; percent: number }>
  by_client: Array<{ client_id: number; client_name: string; count: number; percent: number }>
}

export type ActiveEmploymentFilterOptions = {
  outcomes: EmploymentVerificationOutcome[]
  verify_methods: EmploymentVerificationMethod[]
  factory_statuses: string[]
  staff: Array<{ id: number; name: string; employee_status?: string | null }>
  clients: Array<{ id: number; name: string }>
  can_filter_staff: boolean
  only_mine_default: boolean
}

export type ActiveEmploymentListParams = {
  page?: number
  per_page?: number
  verified_from?: string
  verified_to?: string
  search?: string
  client_id?: number
  recruiter_id?: number
  factory_status?: string
  factory_confirm_status?: FactoryConfirmStatus
  latest_outcome?: EmploymentVerificationOutcome
  never_verified?: boolean
  only_mine?: boolean
}

type PaginatedMeta = {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export const activeEmploymentApi = {
  async dashboard(params?: { verified_from?: string; verified_to?: string }) {
    const { data } = await http.get('/recruitment/active-employment/dashboard', { params })
    return data as { data: ActiveEmploymentDashboard }
  },

  async list(params: ActiveEmploymentListParams) {
    const { data } = await http.get('/recruitment/active-employment', { params })
    return data as { data: ActiveEmploymentRow[]; meta: PaginatedMeta }
  },

  async summary(params: Omit<ActiveEmploymentListParams, 'page' | 'per_page'>) {
    const { data } = await http.get('/recruitment/active-employment/summary', { params })
    return data as { data: ActiveEmploymentSummary }
  },

  async filterOptions(_force = false) {
    const { data } = await http.get('/recruitment/active-employment/filter-options')
    return data as { data: ActiveEmploymentFilterOptions }
  },

  async verifications(employmentId: number) {
    const { data } = await http.get(`/recruitment/active-employment/${employmentId}/verifications`)
    return data as { data: EmploymentVerification[] }
  },

  async storeVerification(
    employmentId: number,
    payload: {
      outcome: EmploymentVerificationOutcome
      verify_method?: EmploymentVerificationMethod
      note?: string
      next_follow_at?: string
      confidence?: number
    },
  ) {
    const { data } = await http.post(
      `/recruitment/active-employment/${employmentId}/verifications`,
      payload,
    )
    return data as { data: EmploymentVerification }
  },

  async createFollowUpApplication(employmentId: number, jobOrderId?: number) {
    const { data } = await http.post(
      `/recruitment/active-employment/${employmentId}/follow-up-application`,
      jobOrderId != null ? { job_order_id: jobOrderId } : {},
    )
    return data as { data: { application_id: number; worker_id: number; status: string } }
  },
}
